import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BedDouble, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';
import {
  STAY_NIGHTS_MAX,
  STAY_NIGHTS_MIN,
  type StayConflictPreview,
} from '@/lib/allocation/stay';
import {
  useDisruptionStay,
  useSetDisruptionNights,
  useStayPlanChannel,
} from '@/lib/allocation/use-stay';
import type { DeskState } from './desk-model';

// THE disruption-level Nights control (feature `stay-nights`, slice N8) — the
// operator says "this disruption is N nights" once, and every group without its
// own evidence books that stay.
//
// WHY IT LIVES ON THE DESK HEADER AND NOT IN THE ACTION BAR
// The spec sketched this into `event-allocation-action-bar.tsx`, next to
// "Allocate hotels". It cannot honestly live there: that bar is handed a
// `disruptionUrn` and nothing else, and the ONE thing this control must show
// before it writes is the CONFLICT COUNT — how many PNRs already carry
// flight-derived evidence implying a different number (stay-nights.md §7: "the UI
// shows the conflict count BEFORE they confirm, so good data is never silently
// erased"). That count is computed over the roster the desk has already walked.
// Putting the control where the data is keeps one read; putting it in the action
// bar would mean walking the roster a second time to say the same thing. It also
// reads better: the desk header already states the shift's goal in PNRs and pax —
// "and it is a three-night stay" belongs in that sentence.
//
// SAFETY. A blanket number NEVER erases a per-PNR one a human set: `OPERATOR_GROUP`
// outranks `OPERATOR_DISRUPTION` in the ladder, cases enforces it, and the preview
// says how many groups will keep their own so the operator is not surprised after
// the fact. When the number would overwrite real flight-derived evidence the
// control refuses to fire on one click — it states the cost and asks again.
//
// Copy is i18n (`tower:desk.stay.*`, `cases:stayPlan.*`); this file holds no prose.

export function StayNightsControl({ desk }: { desk: DeskState }) {
  const { t } = useTranslation('tower');
  // The desk is disruption-scoped, and cases denormalises the parent disruption
  // onto every roster row — so the URN is on the rows the walk already produced.
  // Absent (a standalone case's rows) ⇒ there is no disruption to set, and the
  // control stays out of the way rather than rendering a dead button.
  const disruptionUrn = desk.rows.find((d) => d.row.disruptionUrn)?.row.disruptionUrn;
  const caseUrns = useMemo(() => desk.rows.map((d) => d.row.caseUrn), [desk.rows]);
  const stay = useDisruptionStay(caseUrns);
  useStayPlanChannel(disruptionUrn);

  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState(false);
  const setNights = useSetDisruptionNights(disruptionUrn);

  const parsed = Number.parseInt(draft, 10);
  const valid =
    Number.isInteger(parsed) && parsed >= STAY_NIGHTS_MIN && parsed <= STAY_NIGHTS_MAX;
  const preview: StayConflictPreview | null = valid ? stay.preview(parsed) : null;
  const conflicts = preview?.conflicts ?? 0;

  if (!disruptionUrn) return null;

  const onSubmit = (): void => {
    if (!valid || setNights.isPending) return;
    // One click is enough when nothing good is at stake. When flight-derived
    // evidence WOULD be overwritten, the first click only states the cost.
    if (conflicts > 0 && !confirming) {
      setConfirming(true);
      return;
    }
    setNights.mutate(parsed, {
      onSuccess: () => {
        setConfirming(false);
        setDraft('');
      },
    });
  };

  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border/70 pt-2.5 text-xs"
      data-testid="stay-nights-control"
    >
      <span className="inline-flex items-center gap-1.5 font-medium">
        <BedDouble className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        {t('desk.stay.label')}
      </span>

      {/* Current state — the four states, in the order the operator meets them. */}
      <span className="text-muted-foreground" data-testid="stay-nights-current">
        {stay.isLoading ? (
          <span className="inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> {t('desk.stay.loading')}
          </span>
        ) : stay.isError ? (
          <span className="text-destructive">{t('desk.stay.readError')}</span>
        ) : stay.groups.length === 0 ? (
          t('desk.stay.none')
        ) : stay.nights !== null ? (
          t('desk.stay.current', { count: stay.nights })
        ) : (
          t('desk.stay.mixed', { count: stay.groups.length })
        )}
        {stay.withoutPlan > 0 ? ` · ${t('desk.stay.withoutPlan', { count: stay.withoutPlan })}` : ''}
        {stay.truncated ? ` · ${t('desk.stay.truncated')}` : ''}
      </span>

      <span className="ml-auto flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5">
          <span className="sr-only">{t('desk.stay.inputAria')}</span>
          <input
            type="number"
            inputMode="numeric"
            min={STAY_NIGHTS_MIN}
            max={STAY_NIGHTS_MAX}
            step="1"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setConfirming(false);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onSubmit();
            }}
            placeholder={t('desk.stay.placeholder')}
            aria-label={t('desk.stay.inputAria')}
            className="w-16 rounded-md border border-input bg-background px-2 py-1 tabular-nums"
            data-testid="stay-nights-input"
          />
        </label>
        <Button
          size="sm"
          variant={confirming ? 'destructive' : 'outline'}
          disabled={!valid || setNights.isPending}
          onClick={onSubmit}
          title={t('desk.stay.applyTitle')}
          data-testid="stay-nights-apply"
        >
          {setNights.isPending ? (
            <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden />
          ) : null}
          {confirming ? t('desk.stay.applyAnyway') : t('desk.stay.apply')}
        </Button>
        {confirming ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirming(false)}
            data-testid="stay-nights-cancel"
          >
            {t('desk.stay.cancel')}
          </Button>
        ) : null}
      </span>

      {/* The pre-confirm cost, stated before the write — never after it. */}
      {preview ? (
        <p
          className={cn(
            'w-full',
            conflicts > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground',
          )}
          data-testid="stay-nights-preview"
        >
          {conflicts > 0 ? (
            <AlertTriangle className="mr-1 inline h-3 w-3 align-[-2px]" aria-hidden />
          ) : null}
          {conflicts > 0
            ? t('desk.stay.previewConflicts', { count: conflicts, nights: parsed })
            : t('desk.stay.previewClean', { count: preview.considered, nights: parsed })}
          {preview.overridden > 0
            ? ` ${t('desk.stay.previewOverridden', { count: preview.overridden })}`
            : ''}
        </p>
      ) : null}

      {/* Success + failure, both stated on the control that caused them. Every
          figure is read defensively: `data` is the BFF's summary, and a 200 with
          a thin body must leave the desk standing, not throw inside a render. */}
      {setNights.isSuccess && !preview ? (
        <p className="w-full text-emerald-700 dark:text-emerald-300" data-testid="stay-nights-done">
          {t('desk.stay.applied', {
            nights: setNights.data?.nights ?? 0,
            groups: setNights.data?.affected ?? 0,
          })}
          {(setNights.data?.overridden ?? 0) > 0
            ? ` ${t('desk.stay.appliedKept', { count: setNights.data?.overridden ?? 0 })}`
            : ''}
          {(setNights.data?.mismatched ?? 0) > 0
            ? ` ${t('desk.stay.appliedMismatched', { count: setNights.data?.mismatched ?? 0 })}`
            : ''}
        </p>
      ) : null}
      {setNights.isError ? (
        <p className="w-full text-destructive" data-testid="stay-nights-error">
          {t('desk.stay.writeError')}
        </p>
      ) : null}

      {/* A night count moved under a hotel that is already at stake. Not a toast —
          it stays until a human resolves it, per PNR, in the drawer. */}
      {stay.mismatches > 0 ? (
        <p
          className="w-full rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
          data-testid="stay-mismatch-banner"
        >
          <AlertTriangle className="mr-1 inline h-3 w-3 align-[-2px]" aria-hidden />
          {t('desk.stay.mismatchBanner', { count: stay.mismatches })}
        </p>
      ) : null}
    </div>
  );
}
